import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FiX } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import { JOURNAL_TAG_COLORS } from '@/lib/journal-tags';
import type { JournalTag } from '@/lib/types';
import { Button, Input } from '@/components';

type JournalTagPickerProps = {
  selectedTagIds: number[];
  onChange: (ids: number[]) => void;
  showManage?: boolean;
};

export function JournalTagPicker({
  selectedTagIds,
  onChange,
  showManage = true,
}: JournalTagPickerProps) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [manageOpen, setManageOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState<string>(JOURNAL_TAG_COLORS[0]);
  const [editingTag, setEditingTag] = useState<JournalTag | null>(null);

  const tagsQuery = useQuery({
    queryKey: ['journal-tags'],
    queryFn: () => apiJson<JournalTag[]>(getToken, '/v1/journal/tags'),
  });

  const tags = tagsQuery.data ?? [];

  const createMutation = useMutation({
    mutationFn: () =>
      apiJson<JournalTag>(getToken, '/v1/journal/tags', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: newLabel.trim(), color: newColor }),
      }),
    onSuccess: (tag) => {
      void queryClient.invalidateQueries({ queryKey: ['journal-tags'] });
      onChange([...selectedTagIds, tag.id]);
      setNewLabel('');
      setManageOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!editingTag) throw new Error('No tag');
      return apiJson<JournalTag>(getToken, `/v1/journal/tags/${editingTag.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: newLabel.trim() || editingTag.label,
          color: newColor,
        }),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['journal-tags'] });
      setEditingTag(null);
      setNewLabel('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (tagId: number) =>
      apiJson(getToken, `/v1/journal/tags/${tagId}`, { method: 'DELETE' }),
    onSuccess: (_data, tagId) => {
      void queryClient.invalidateQueries({ queryKey: ['journal-tags'] });
      onChange(selectedTagIds.filter((id) => id !== tagId));
    },
  });

  const toggleTag = (tagId: number) => {
    if (selectedTagIds.includes(tagId)) {
      onChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onChange([...selectedTagIds, tagId]);
    }
  };

  return (
    <div>
      <div className='flex flex-wrap gap-2'>
        {tags.map((tag) => {
          const selected = selectedTagIds.includes(tag.id);
          return (
            <button
              key={tag.id}
              type='button'
              onClick={() => toggleTag(tag.id)}
              className={`px-3 py-1 rounded-full text-sm font-semibold border transition-colors ${
                selected ? 'text-white border-transparent' : 'bg-white text-gray-700 border-gray-200'
              }`}
              style={selected ? { backgroundColor: tag.color } : undefined}
            >
              {tag.label}
            </button>
          );
        })}
        {tags.length === 0 && (
          <p className='text-sm text-gray-400'>No tags yet.</p>
        )}
      </div>
      {showManage && (
        <button
          type='button'
          onClick={() => {
            setEditingTag(null);
            setNewLabel('');
            setNewColor(JOURNAL_TAG_COLORS[0]);
            setManageOpen(true);
          }}
          className='mt-2 text-sm text-primary font-semibold hover:underline'
        >
          Manage tags
        </button>
      )}

      {manageOpen && (
        <div className='fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-lg font-bold text-gray-900'>Manage tags</h3>
              <button type='button' onClick={() => setManageOpen(false)} className='p-2 hover:bg-gray-100 rounded-full'>
                <FiX className='w-5 h-5' />
              </button>
            </div>

            <div className='space-y-2 mb-4'>
              {tags.map((tag) => (
                <div key={tag.id} className='flex items-center justify-between gap-2 py-1'>
                  <div className='flex items-center gap-2 min-w-0'>
                    <span
                      className='w-3 h-3 rounded-full shrink-0'
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className='text-sm font-medium truncate'>{tag.label}</span>
                  </div>
                  <div className='flex gap-2 shrink-0'>
                    <button
                      type='button'
                      className='text-xs text-primary font-semibold'
                      onClick={() => {
                        setEditingTag(tag);
                        setNewLabel(tag.label);
                        setNewColor(tag.color);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type='button'
                      className='text-xs text-red-600 font-semibold'
                      onClick={() => {
                        if (window.confirm(`Delete tag "${tag.label}"?`)) {
                          deleteMutation.mutate(tag.id);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <Input
              label={editingTag ? 'Edit tag name' : 'New tag name'}
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder='School, medical, etc.'
            />
            <p className='text-sm font-semibold text-gray-600 mt-3 mb-2'>Color</p>
            <div className='flex flex-wrap gap-2'>
              {JOURNAL_TAG_COLORS.map((color) => (
                <button
                  key={color}
                  type='button'
                  onClick={() => setNewColor(color)}
                  className={`w-8 h-8 rounded-full border-2 ${
                    newColor === color ? 'border-gray-900' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color }}
                  aria-label={`Color ${color}`}
                />
              ))}
            </div>
            <div className='flex gap-3 mt-6'>
              {editingTag ? (
                <>
                  <Button variant='secondary' fullWidth onClick={() => setEditingTag(null)}>
                    Cancel edit
                  </Button>
                  <Button
                    fullWidth
                    onClick={() => updateMutation.mutate()}
                    loading={updateMutation.isPending}
                    disabled={!newLabel.trim()}
                  >
                    Save tag
                  </Button>
                </>
              ) : (
                <Button
                  fullWidth
                  onClick={() => createMutation.mutate()}
                  loading={createMutation.isPending}
                  disabled={!newLabel.trim()}
                >
                  Add tag
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
